/*
  pfc-pers-fit-core.js — printed mount text: fit by PHYSICAL SPACE, not a
  character count.

  Shared by the three customisers that offer "Add printed text or a logo":
  pfc-moulding.js, pfc-mount.js and pfc-print-app.js. Load it BEFORE them.

  THE MODEL
  - The size button (XS/S/M/L = 3.5/5/7/9 mm, the font EM size) is EXACTLY what
    prints. Text is never silently shrunk. 3.5 mm is the floor (owner decision
    2026-09-17, after a physical test print: customers use small text as a
    caption, so going small is their call).
  - Both lines print at that one size.
  - What varies is how MUCH text fits: the zone is the opening (photo) width,
    never wider, and the bottom-border band height. A line may not grow past
    the zone width; a size whose text would not fit is greyed out.
  - Changing orientation / size / mount never deletes text: the design is
    flagged as over-size and add-to-cart holds until it is fixed.

  MEASUREMENT
  Widths are measured with canvas measureText at a large reference size and
  scaled linearly (advance widths are linear in font size), taking the larger of
  the advance width and the INK extent so script swashes are included. Measured
  2026-09-17: Chrome and node-canvas (the print-file app) return identical
  advance widths for all 12 fonts, so what fits here fits there.
  Measuring is refused until the chosen font has actually loaded — measuring
  the fallback face gives the wrong answer.

  The height rule mirrors drawTextBlock and the print app's
  renderPersonalisationText exactly: size <= 0.74 * zoneH / (lines + 0.4).
  Because the gate guarantees both rules hold, those renderers' own shrink loops
  never engage, so preview, cart image and print file all use the chosen size.

  SWITCH (fully revertable)
  Off unless the theme setting `pers_fit_v2` is ticked (passed on this script
  tag as data-enabled) or the page URL carries ?pers_fit=1. ?pers_fit=0 forces
  it off. When off, every customiser runs its previous code path unchanged.

  Dual export: browser global `PFCPersFit`, or `require()` under node for tests.
*/
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.PFCPersFit = api;
})(typeof self !== 'undefined' ? self : this, function (root) {
  'use strict';

  var SIZES_MM = { xs: 3.5, s: 5, m: 7, l: 9 };
  var SIZE_ORDER = ['xs', 's', 'm', 'l'];
  var SIZE_LABEL = { xs: 'XS', s: 'S', m: 'M', l: 'L' };
  var FILL = 0.74;            // em as a share of a line slot (drawTextBlock)
  var LINE_SLOTS_PAD = 0.4;   // drawTextBlock: lineH = h / (n + 0.4)
  var LOGO_SHARE = 0.42, LOGO_GAP = 0.06;   // drawPersonalisation text+logo split
  var REF_PX = 400;           // measure here, scale linearly
  var HARD_MAX_CHARS = 150;   // safety ceiling only; the real limit is width
  var EPS = 1e-6;
  // Average-width sample for the "≈ N left" estimate: mixed case, digits and
  // spaces, like real mount text. Only the ESTIMATE uses it; the hard limit is
  // always the true measured width of the actual text.
  var SAMPLE = 'The Smith Family est. 2026 in loving memory';

  /* ---------- switch ---------- */
  var scriptEnabled = false;
  try {
    var cs = root && root.document && root.document.currentScript;
    scriptEnabled = !!(cs && cs.dataset && cs.dataset.enabled === 'true');
  } catch (_) {}
  function urlOverride() {
    try {
      var m = /[?&]pers_fit=([01])(?:&|$)/.exec(root.location.search);
      return m ? m[1] === '1' : null;
    } catch (_) { return null; }
  }
  var ov = urlOverride();
  var ENABLED = ov === null ? scriptEnabled : ov;

  /* ---------- measurement ---------- */
  var cache = {};
  function fontSpec(px, familyCss) { return '600 ' + px + 'px ' + familyCss; }
  // Metrics at REF_PX: advance, and how far the INK overhangs the advance box
  // on each side (script swashes do; most fonts don't).
  function refMetrics(ctx, familyCss, text) {
    var key = familyCss + '|' + text;
    if (cache[key]) return cache[key];
    ctx.font = fontSpec(REF_PX, familyCss);
    ctx.textAlign = 'left';
    var m = ctx.measureText(text);
    var adv = m.width || 0;
    var overL = Math.max(0, m.actualBoundingBoxLeft || 0);
    var overR = Math.max(0, (m.actualBoundingBoxRight || 0) - adv);
    // Width budget: the advance plus the larger overhang on BOTH sides. Centred
    // text then stays inside the zone; left/right text does too once the band
    // is inset by that side's overhang (alignInsetMm).
    var r = { adv: adv, overL: overL, overR: overR, w: adv + 2 * Math.max(overL, overR) };
    cache[key] = r;
    return r;
  }
  function refWidth(ctx, familyCss, text) { return refMetrics(ctx, familyCss, text).w; }
  // Renderers align by the ADVANCE box, so a left-aligned swash pokes out to the
  // left and a right-aligned one to the right. Inset the band on the aligned
  // side by the largest overhang of any line; centred text needs no inset.
  function alignInsetMm(ctx, familyCss, lines, sizeMm, align) {
    var l = 0, r = 0;
    (lines || []).forEach(function (t) {
      if (!t || !t.trim()) return;
      var m = refMetrics(ctx, familyCss, t);
      if (align === 'left') l = Math.max(l, m.overL);
      else if (align === 'right') r = Math.max(r, m.overR);
    });
    return { l: l * sizeMm / REF_PX, r: r * sizeMm / REF_PX };
  }
  function widthMm(ctx, familyCss, text, sizeMm) {
    if (!text) return 0;
    return refWidth(ctx, familyCss, text) * sizeMm / REF_PX;
  }
  function avgCharMm(ctx, familyCss, sizeMm) {
    return refWidth(ctx, familyCss, SAMPLE) / SAMPLE.length * sizeMm / REF_PX;
  }
  function maxSizeForHeight(zoneHmm, nLines) {
    return FILL * zoneHmm / ((nLines || 1) + LINE_SLOTS_PAD);
  }

  /* ---------- zone geometry (mm) ---------- */
  // Usable text width: the opening width, but never into the rebate-safe edge.
  function zoneWidthMm(apertureWmm, boardWmm, edgeMm, padMm) {
    return Math.max(0, Math.min(apertureWmm, boardWmm - 2 * edgeMm - 2 * padMm));
  }
  // Height the TEXT gets inside a band (drawPersonalisation's split).
  function textZoneHeightMm(bandHmm, padMm, hasLogo) {
    var inner = bandHmm - 2 * padMm;
    if (inner <= 0) return 0;
    return hasLogo ? inner * (1 - LOGO_SHARE - LOGO_GAP) : inner;
  }
  // Horizontal band (x from the board's left edge, and width) whose inner area
  // (band minus the box pad) is the zone, centred on the board — the opening is
  // centred horizontally on every mount we cut.
  function bandXmm(boardWmm, zoneWmm, padMm) {
    return { x: (boardWmm - zoneWmm) / 2 - padMm, w: zoneWmm + 2 * padMm };
  }

  /* ---------- fit ---------- */
  // o: { lines: [heading, line2], familyCss, sizeMm, zoneWmm, zoneHmm }
  function fit(ctx, o) {
    var raw = o.lines || [];
    var n = raw.filter(function (t) { return t && t.trim(); }).length;
    var maxH = maxSizeForHeight(o.zoneHmm, n || 1);
    var heightOk = o.sizeMm <= maxH + EPS;
    var avg = avgCharMm(ctx, o.familyCss, o.sizeMm);
    var widthOk = true;
    var lines = raw.map(function (t) {
      var w = (t && t.trim()) ? widthMm(ctx, o.familyCss, t, o.sizeMm) : 0;
      var fits = w <= o.zoneWmm + EPS;
      if (!fits) widthOk = false;
      var left = avg > 0 ? Math.floor(Math.max(0, o.zoneWmm - w) / avg) : 0;
      left = Math.min(left, Math.max(0, HARD_MAX_CHARS - (t ? t.length : 0)));
      return { widthMm: w, fits: fits, left: left };
    });
    return {
      ok: heightOk && widthOk, heightOk: heightOk, widthOk: widthOk,
      maxHeightSizeMm: maxH, lines: lines, n: n
    };
  }
  function sizeStates(ctx, o) {
    var out = {};
    SIZE_ORDER.forEach(function (k) {
      out[k] = fit(ctx, {
        lines: o.lines, familyCss: o.familyCss, sizeMm: SIZES_MM[k],
        zoneWmm: o.zoneWmm, zoneHmm: o.zoneHmm
      });
    });
    return out;
  }
  // Longest insertion that still fits. prev/next are whole field values.
  function clampInsertion(ctx, o, idx, prev, next) {
    var p = 0;
    while (p < prev.length && p < next.length && prev[p] === next[p]) p++;
    var s = 0;
    while (s < prev.length - p && s < next.length - p &&
           prev[prev.length - 1 - s] === next[next.length - 1 - s]) s++;
    var head = next.slice(0, p), ins = next.slice(p, next.length - s), tail = next.slice(next.length - s);
    function ok(k) {
      var cand = head + ins.slice(0, k) + tail;
      if (cand.length > HARD_MAX_CHARS) return false;
      return widthMm(ctx, o.familyCss, cand, o.sizeMm) <= o.zoneWmm + EPS;
    }
    var lo = 0, hi = ins.length;
    while (lo < hi) { var mid = (lo + hi + 1) >> 1; if (ok(mid)) lo = mid; else hi = mid - 1; }
    return { value: head + ins.slice(0, lo) + tail, caret: p + lo, cut: lo < ins.length };
  }

  /* ---------- fonts ---------- */
  function fontReady(family) {
    try {
      var d = root.document;
      if (!d || !d.fonts || !d.fonts.check) return true;
      return d.fonts.check(fontSpec(20, '"' + family + '"'));
    } catch (_) { return true; }
  }
  function loadFont(family) {
    try {
      var d = root.document;
      if (!d || !d.fonts || !d.fonts.load) return Promise.resolve();
      return d.fonts.load(fontSpec(20, '"' + family + '"')).then(function () {}, function () {});
    } catch (_) { return Promise.resolve(); }
  }

  /* ---------- styles (injected once, px only — see CLAUDE.md) ---------- */
  var CSS = [
    '.pfc-pf-count{display:block;margin-top:4px;font-size:11.5px;line-height:1.3;color:#9b8f7c;font-variant-numeric:tabular-nums;transition:color .2s}',
    '.pfc-pf-count.is-full{color:#8a5d28}',
    '.pfc-pf-count.is-over{color:#b3261e}',
    '.pfc-pf-pulse{animation:pfcPfPulse .6s ease}',
    '@keyframes pfcPfPulse{0%{transform:scale(1)}35%{transform:scale(1.08);color:#8a5d28}100%{transform:scale(1)}}',
    '.pfc-pf-count{transform-origin:left center}',
    '[data-pers-size-group] button{transition:opacity .35s ease,background-color .2s,color .2s}',
    '[data-pers-size-group] button.pfc-pf-flip{animation:pfcPfFlip .5s ease}',
    '@keyframes pfcPfFlip{0%{transform:scale(1)}40%{transform:scale(.86)}100%{transform:scale(1)}}',
    '[data-pers-size-group] button.is-over{box-shadow:inset 0 0 0 2px #b3261e}',
    '.pfc-pf-shake{animation:pfcPfShake .3s ease}',
    '@keyframes pfcPfShake{0%,100%{transform:translateX(0)}25%{transform:translateX(-3px)}75%{transform:translateX(3px)}}',
    '.pfc-pf-over-note{margin:8px 0 0;font-size:12px;line-height:1.35;color:#b3261e}',
    '.pfc-pf-input-over{border-color:#b3261e !important}',
    '.pfc-pf-count[hidden],.pfc-pf-over-note[hidden]{display:none !important}'
  ].join('\n');
  function injectCss() {
    var d = root.document;
    if (!d || d.getElementById('pfc-pers-fit-css')) return;
    var st = d.createElement('style');
    st.id = 'pfc-pers-fit-css';
    st.textContent = CSS;
    (d.head || d.documentElement).appendChild(st);
  }
  function flash(el, cls, ms) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;   // restart the animation
    el.classList.add(cls);
    setTimeout(function () { el.classList.remove(cls); }, ms || 600);
  }

  /* ---------- UI controller ----------
     o: {
       panel:     element holding the text fields (the pers panel),
       state:     function () -> state.pers,
       zoneMm:    function () -> { w, h } | null   (null = no mount/aperture yet),
       familyCss: function (fontName) -> CSS family list,
       sizeBtns:  [button], sizeNote: element|null,
       heading, line2: input elements,
       onSize:    function (key) — select a size the way the customiser does,
       rerender:  function () — redraw the preview
     }
     Returns an object with .enabled; every method is a safe no-op when off. */
  function controller(o) {
    var api = {
      enabled: ENABLED,
      update: function () {},
      addError: function () { return null; },
      exact: function () { return false; }
    };
    if (!ENABLED || !o || !root.document) return api;
    injectCss();
    var d = root.document;
    var ctx = d.createElement('canvas').getContext('2d');
    var inputs = [o.heading, o.line2];
    var counts = [], lastGood = [];
    var prevDisabled = {}, prevLeft = [null, null], lastReason = null;
    var pendingFont = null;
    // Until the customer picks a size, the default (M) is a ceiling, not a
    // promise: it steps down to the largest size that fits, and back up to the
    // default when the text is shortened. Once they pick, their size is exact.
    var userPicked = false;
    var defaultSize = o.state().size || 'm';
    o.sizeBtns.forEach(function (b) {
      b.addEventListener('click', function () { userPicked = true; }, true);
    });

    // XS is new: shown only when this model is on (the legacy code knows no XS).
    o.sizeBtns.forEach(function (b) { if (b.dataset.persSize === 'xs') b.hidden = false; });
    // The legacy "Auto-sized to fit" note has a display rule that beats [hidden].
    if (o.sizeNote) { o.sizeNote.hidden = true; o.sizeNote.style.display = 'none'; }

    inputs.forEach(function (inp, i) {
      if (!inp) return;
      inp.maxLength = HARD_MAX_CHARS;
      lastGood[i] = inp.value || '';
      var c = d.createElement('span');
      c.className = 'pfc-pf-count';
      c.setAttribute('aria-live', 'polite');
      inp.insertAdjacentElement('afterend', c);
      counts[i] = c;
      // Capture phase: runs before the customiser's own input listener, so a
      // rejected keystroke never reaches its state.
      inp.addEventListener('input', function (e) {
        var z = o.zoneMm(), st = o.state();
        if (!z || !fontReady(st.font)) { lastGood[i] = inp.value; return; }
        var args = { lines: [o.heading ? o.heading.value : '', o.line2 ? o.line2.value : ''],
                     familyCss: o.familyCss(st.font), sizeMm: SIZES_MM[st.size] || SIZES_MM.m,
                     zoneWmm: z.w, zoneHmm: z.h };
        var next = inp.value, prev = lastGood[i];
        var wNext = widthMm(ctx, args.familyCss, next, args.sizeMm);
        var wPrev = widthMm(ctx, args.familyCss, prev, args.sizeMm);
        // Accept anything that fits, and any edit that doesn't make an
        // already-too-long line longer (so deleting always works).
        if ((wNext <= z.w + EPS && next.length <= HARD_MAX_CHARS) || wNext <= wPrev + EPS) {
          lastGood[i] = next; lastReason = 'type'; return;
        }
        var r = clampInsertion(ctx, args, i, prev, next);
        inp.value = r.value;
        try { inp.setSelectionRange(r.caret, r.caret); } catch (_) {}
        flash(counts[i], 'pfc-pf-shake', 320);
        if (r.value === prev) { e.stopImmediatePropagation(); return; }
        lastGood[i] = r.value; lastReason = 'type';
      }, true);
    });

    var overNote = d.createElement('p');
    overNote.className = 'pfc-pf-over-note';
    overNote.hidden = true;
    overNote.setAttribute('role', 'status');
    var group = o.sizeBtns.length ? o.sizeBtns[0].parentNode : null;
    var host = group && group.parentNode;
    if (host) host.appendChild(overNote);

    function current() {
      var st = o.state(), z = o.zoneMm();
      if (!z) return null;
      return {
        st: st, z: z,
        args: { lines: [st.heading || '', st.line2 || ''], familyCss: o.familyCss(st.font),
                zoneWmm: z.w, zoneHmm: z.h }
      };
    }
    // `anyFits` = some size still prints this text on this mount. When NOTHING
    // fits, pointing at a smaller size is a dead end (that was the bug: every
    // button greyed, including the selected one, with no way out), so the
    // message names the only real fixes and no button is greyed.
    function problem(f, size, anyFits) {
      if (!f || f.ok) return null;
      var lab = SIZE_LABEL[size] || 'this size';
      var smallest = size === SIZE_ORDER[0];
      var st = o.state();
      if (!f.heightOk) {
        var fixes = [];
        if (anyFits && !smallest) fixes.push('choose a smaller size');
        if (f.n > 1) fixes.push('clear the second line');
        if (st.logoOn) fixes.push('remove the logo');
        fixes.push('pick a larger mount or a smaller opening');
        return 'Too tall to print here — ' + fixes.join(', or ') + '.';
      }
      var wfix = [];
      wfix.push('shorten the text');
      if (anyFits && !smallest) wfix.push('choose a smaller size');
      else if (!anyFits) wfix.push('pick a larger mount or a smaller opening');
      return 'Too long to print at ' + lab + ' here — ' + wfix.join(', or ') + '.';
    }

    api.exact = function () { return true; };

    api.update = function (reason) {
      reason = reason || lastReason || 'other';
      lastReason = null;
      var c = current();
      if (!c) {
        counts.forEach(function (el) { if (el) el.hidden = true; });
        overNote.hidden = true;
        return;
      }
      if (!fontReady(c.st.font)) {
        counts.forEach(function (el) { if (el) { el.hidden = false; el.textContent = 'Measuring…'; } });
        if (pendingFont !== c.st.font) {
          pendingFont = c.st.font;
          loadFont(c.st.font).then(function () { pendingFont = null; api.update('font'); o.rerender && o.rerender(); });
        }
        return;
      }
      var states = sizeStates(ctx, c.args);
      var hasAny = !!((c.st.heading || '').trim() || (c.st.line2 || '').trim());
      if (!userPicked && hasAny) {
        var cap = SIZE_ORDER.indexOf(defaultSize), best = null;
        for (var si = cap; si >= 0; si--) { if (states[SIZE_ORDER[si]].ok) { best = SIZE_ORDER[si]; break; } }
        if (best && best !== c.st.size) {
          c.st.size = best;
          var nb = o.sizeBtns.filter(function (b) { return b.dataset.persSize === best; })[0];
          flash(nb, 'pfc-pf-flip', 500);
          setTimeout(function () { o.rerender && o.rerender(); }, 0);
        }
      }
      var cur = states[c.st.size] || states.m;

      // Counters.
      cur.lines.forEach(function (ln, i) {
        var el = counts[i];
        if (!el) return;
        el.hidden = false;
        var over = !ln.fits;
        el.classList.toggle('is-over', over);
        el.classList.toggle('is-full', !over && ln.left === 0);
        el.textContent = over ? 'Too long at this size'
          : ln.left === 0 ? 'Line full at this size'
          : '≈ ' + ln.left + ' more character' + (ln.left === 1 ? '' : 's');
        if (inputs[i]) inputs[i].classList.toggle('pfc-pf-input-over', over);
        // Pulse when the room changed for a reason other than this line's own typing.
        if (prevLeft[i] !== null && prevLeft[i] !== ln.left && reason !== 'type') flash(el, 'pfc-pf-pulse', 600);
        prevLeft[i] = ln.left;
      });

      // Size buttons: grey out sizes the current text cannot print at — but only
      // while at least one size still can, so the customer is never left with
      // every button disabled and nothing to click.
      var hasText = !!((c.st.heading || '').trim() || (c.st.line2 || '').trim());
      var anyFits = SIZE_ORDER.some(function (k) { return hasText ? states[k].ok : states[k].heightOk; });
      o.sizeBtns.forEach(function (b) {
        var k = b.dataset.persSize, f = states[k];
        if (!f) return;
        var isCur = k === c.st.size;
        var unfit = hasText ? !f.ok : !f.heightOk;
        var dis = unfit && !isCur && anyFits;
        if (prevDisabled[k] !== undefined && prevDisabled[k] !== dis) flash(b, 'pfc-pf-flip', 500);
        prevDisabled[k] = dis;
        b.disabled = dis;
        b.classList.toggle('is-over', isCur && unfit);
        b.setAttribute('aria-pressed', isCur ? 'true' : 'false');
        b.title = unfit
          ? (f.heightOk ? 'Text is too long to print at ' + SIZE_LABEL[k] + ' on this mount'
                        : SIZE_LABEL[k] + ' is too tall for this mount')
          : SIZE_LABEL[k] + ' — ' + SIZES_MM[k] + ' mm text';
      });

      var msg = hasText ? problem(cur, c.st.size, anyFits) : null;
      overNote.hidden = !msg;
      overNote.textContent = msg || '';
    };

    api.addError = function () {
      var c = current();
      if (!c) return null;
      if (!((c.st.heading || '').trim() || (c.st.line2 || '').trim())) return null;
      if (!fontReady(c.st.font)) {
        loadFont(c.st.font).then(function () { api.update('font'); });
        return 'Still loading the font for your text — please try again in a moment.';
      }
      var f = fit(ctx, { lines: c.args.lines, familyCss: c.args.familyCss,
                         sizeMm: SIZES_MM[c.st.size] || SIZES_MM.m,
                         zoneWmm: c.z.w, zoneHmm: c.z.h });
      var states2 = sizeStates(ctx, c.args);
      var anyFits2 = SIZE_ORDER.some(function (k) { return states2[k].ok; });
      var msg = problem(f, c.st.size, anyFits2);
      if (msg) { flash(overNote, 'pfc-pf-shake', 320); api.update('add'); }
      return msg;
    };

    return api;
  }

  return {
    ENABLED: ENABLED,
    SIZES_MM: SIZES_MM,
    SIZE_ORDER: SIZE_ORDER,
    HARD_MAX_CHARS: HARD_MAX_CHARS,
    FILL: FILL,
    widthMm: widthMm,
    alignInsetMm: alignInsetMm,
    maxSizeForHeight: maxSizeForHeight,
    zoneWidthMm: zoneWidthMm,
    textZoneHeightMm: textZoneHeightMm,
    bandXmm: bandXmm,
    fit: fit,
    sizeStates: sizeStates,
    clampInsertion: clampInsertion,
    fontReady: fontReady,
    controller: controller
  };
});
